'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import SmartLogo from '@/components/tool/SmartLogo';
import { getToolUrlPath } from '@/lib/data';
import { usePathname } from 'next/navigation';
import { getLocaleFromPathname, translations, type Dictionary } from '@/lib/i18n';

interface SubcategoryToolsListProps {
  tools: any[];
  localePrefix: string;
  localizedSlug: string;
  subcategory: string;
}

export default function SubcategoryToolsList({
  tools,
  localePrefix,
  localizedSlug,
  subcategory,
}: SubcategoryToolsListProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname || '');
  const t: Dictionary = translations[locale] || translations['en'];
  const [visibleCount, setVisibleCount] = useState(8);

  const showMore = () => {
    setVisibleCount((prev) => prev + 12);
  };

  const visibleTools = tools.slice(0, visibleCount);
  const hasMore = visibleCount < tools.length;

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
        {visibleTools.map((tool) => {
          const toolCats = Array.isArray(tool.categories) ? tool.categories : [];
          const pathSuffix = getToolUrlPath(tool.profession_slug, toolCats, tool.slug);
          return (
          <Link
            key={tool.slug}
            href={`${localePrefix}/ai-for-${localizedSlug}/${pathSuffix}`}
            className="group block"
          >
            <Card className="h-full border-border/60 bg-card/60 backdrop-blur-sm transition-all duration-500 hover:shadow-lg hover:shadow-primary/10 hover:border-primary/30 hover:-translate-y-1.5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] rounded-3xl overflow-hidden">
              <CardContent className="p-4 sm:p-6 space-y-2 sm:space-y-4">
                <div className="flex min-w-0 items-center justify-between gap-2">
                  {/* Logo: hidden on mobile for fast render */}
                  <div className="hidden sm:block h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-border/40 bg-muted/40 p-1.5 shadow-sm transition-transform group-hover:scale-110 group-hover:rotate-3 duration-500 relative">
                    <SmartLogo
                      logoUrl={tool.logo_url}
                      websiteUrl={tool.website_url}
                      name={tool.name}
                      logoAlt={tool.logo_alt}
                      className="object-contain"
                    />
                  </div>
                  <Badge variant="outline" className="min-w-0 truncate text-[0.625rem] font-semibold border-info/30 text-info bg-info/5 uppercase tracking-wide">
                    {tool.jurisdiction || t.toolBadges.global}
                  </Badge>
                </div>
                <h3 className="min-w-0 text-sm sm:text-base font-bold text-foreground group-hover:text-primary transition-colors tracking-tight line-clamp-2">
                  {tool.name}
                </h3>
                {/* Description: hidden on mobile for lightweight rendering */}
                <p className="hidden sm:block text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                  {tool.description_short}
                </p>
                <div className="flex flex-wrap items-center gap-2 pt-1 sm:pt-2">
                  <Badge variant="secondary" className="text-[0.625rem] font-bold uppercase tracking-wider bg-muted text-muted-foreground">
                    {tool.pricing?.[0]?.plan || t.toolBadges.paid}
                  </Badge>
                  {tool.best_for && (
                    <Badge variant="outline" className="text-[10px] lowercase first-letter:uppercase max-w-[120px] truncate hidden sm:flex">
                      {tool.best_for}
                    </Badge>
                  )}
                  {tool.is_sponsored && (
                    <Badge className="text-[0.625rem] font-bold uppercase tracking-wider bg-primary text-primary-foreground shadow-sm">
                      {t.toolBadges.sponsored}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </Link>
          );
        })}
      </div>

      {hasMore && (
        <div className="mt-12 flex justify-center">
          <Button
            onClick={showMore}
            variant="outline"
            size="lg"
            className="rounded-full px-8 font-bold border-primary/20 hover:border-primary/50 hover:bg-primary/5"
          >
            {t.professionPage.showMoreTools.replace('{count}', (tools.length - visibleCount).toString())}
          </Button>
        </div>
      )}
    </div>
  );
}
